// frontend/src/App.js

import React, { useEffect, useState, useContext, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import theme from './theme';
import { getCookieConsentValue, Cookies } from 'react-cookie-consent';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';

import AppContent from './AppContent';
import SessionExpirationListener from './components/SessionExpiredModal/SessionExpirationListener';
import AppProvider from './AppProvider';
import { UIContext } from './contexts/UIContext';
import DocumentationViewer from './components/DocumentationViewer/DocumentationViewer'; // Import the new component
import './components/DocumentationViewer/DocumentationViewer.css';
import ChatFab from './components/Chatbot/ChatFab';
import ChatbotInterface from './components/Chatbot/ChatbotInterface';
import FaqModal from './components/FaqModal/FaqModal';
import CookieConsentModal from './components/CookieConsentModal/CookieConsentModal';
import OnboardingModal from './components/OnboardingModal/OnboardingModal';
import './components/CookieConsentModal/CookieConsentModal.css';

// Import all your page components
import VerificationPage from './pages/VerificationPage/VerificationPage';
import AuthPage from './pages/AuthPage/AuthPage';
import AccountPage from './pages/AccountPage/AccountPage';
import PrivacyPolicy from './pages/TermAndConditions/PrivacyPolicy';
import TermsOfService from './pages/TermAndConditions/TermsOfService';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage';
import AboutPage from './pages/AboutPage/AboutPage';
import IndustryApplications from './pages/IndustryApplications/IndustryApplications';
import IndustryApplicationsAmazon from './pages/IndustryApplicationsAmazon/IndustryApplications';
import IndustryApplicationsFinance from './pages/IndustryApplicationsFinance/IndustryApplications';
import IndustryApplicationsHealthcare from './pages/IndustryApplicationsHealthcare/IndustryApplications';
import EnterprisePage from './pages/EnterprisePage/EnterprisePage';
import FeaturesPage from './pages/FeaturesPage/FeaturesPage';
import TechnologyPage from './pages/TechnologyPage/TechnologyPage';
import PricingPage from './pages/PricingPage/PricingPage';
import ContactPage from './pages/ContactPage/ContactPage';
import HowWeAreDifferentPage from './pages/HowWeAreDifferentPage/HowWeAreDifferentPage';
import LandingPage from './pages/LandingPage/LandingPage';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import SiteLock from './components/SiteLock/SiteLock';
import PublicRoute from './components/PublicRoute/PublicRoute';
import InitializationPage from './pages/InitializationPage/InitializationPage';
import BlogIndexPage from './pages/BlogIndexPage/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage/BlogPostPage';
import AnalysisHistoryPage from './pages/AnalysisHistoryPage/AnalysisHistoryPage';
import BulkAnalysisPage from './pages/BulkAnalysisPage/BulkAnalysisPage';
import LicenseManagementPage from './pages/LicenseManagementPage/LicenseManagementPage';
import BillingPage from './pages/BillingPage/BillingPage';
import ThreatRadarPage from './pages/ThreatRadarPage/ThreatRadarPage';
import { HelmetProvider } from 'react-helmet-async';

const PageWrapper = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
    >
        {children}
    </motion.div>
);

const GlobalLayout = () => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const { isFaqModalOpen, setIsFaqModalOpen } = useContext(UIContext);
    const { isDocViewerOpen, closeDocViewer, initialDocPath } = useContext(UIContext);
    const location = useLocation();
    const toggleChat = () => setIsChatOpen(!isChatOpen);
    const showChatbot = true;
    const highlightChatFab = isFaqModalOpen || location.pathname === '/contact';
    const isToolPage = location.pathname.startsWith('/app') || location.pathname.startsWith('/verify');
    const pageType = isToolPage ? 'tool-page' : 'info-page';

    useEffect(() => {
        // 1. Detect if we are in a development environment
        const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

        // 2. Add the !isDevelopment check to the condition
        if (
            location.pathname === '/app' && 
            localStorage.getItem('hasEverAutoOpenedChat') !== 'true' &&
            !isDevelopment
        ) {
            // Add a 2-second delay before opening the chat
            const timer = setTimeout(() => {
                setIsChatOpen(true);
                // Save to localStorage so it NEVER auto-opens on this browser again
                localStorage.setItem('hasEverAutoOpenedChat', 'true'); 
            }, 2000); // 2000 milliseconds = 2 seconds

            // Cleanup function to clear the timer if the component unmounts
            return () => clearTimeout(timer);
        }
    }, [location.pathname]); // This effect runs every time the page location changes

    return (
        <>
            <Outlet />
            {showChatbot && (
                <>
                    <ChatFab
                        onClick={toggleChat}
                        isOpen={isChatOpen}
                        isFaqHighlighted={highlightChatFab}
                        pageType={pageType}
                    />
                    <ChatbotInterface
                        isOpen={isChatOpen}
                        onClose={() => setIsChatOpen(false)}
                    />
                </>
            )}
            <FaqModal
                isOpen={isFaqModalOpen}
                onClose={() => setIsFaqModalOpen(false)}
            />
            <DocumentationViewer
                isOpen={isDocViewerOpen}
                onClose={closeDocViewer}
                initialPath={initialDocPath}
            />
            <OnboardingModal />
        </>
    );
};

const InfoPageLayout = () => (
    <div className="app-wrapper">
        <Outlet />
    </div>
);

// A simple layout component that applies the SiteLock
const SiteLockedLayout = () => (
    <SiteLock>
        <Outlet />
    </SiteLock>
);

// AppLayout is now a true layout component that renders its children via <Outlet />
const AppLayout = () => {
    // Copy the particle logic from LandingPage.jsx
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    // Full particle configuration object
    const particlesConfig = {
        background: {
            color: {
                value: 'transparent',
            },
        },
        fpsLimit: 120,
        interactivity: {
            events: {
                onHover: {
                    enable: true,
                    mode: 'repulse',
                },
                resize: true,
            },
            modes: {
                repulse: {
                    distance: 100,
                    duration: 0.4,
                },
            },
        },
        particles: {
            color: {
                value: '#ffffff',
            },
            links: {
                color: '#4fc3f7',
                distance: 150,
                enable: true,
                opacity: 0.4,
                width: 1,
            },
            move: {
                direction: 'none',
                enable: true,
                outModes: {
                    default: 'bounce',
                },
                random: false,
                speed: 1,
                straight: false,
            },
            number: {
                density: {
                    enable: true,
                    area: 800,
                },
                value: 80,
            },
            opacity: {
                value: 0.5,
            },
            shape: {
                type: 'circle',
            },
            size: {
                value: { min: 1, max: 3 },
            },
        },
        detectRetina: true,
    };
    return (
        <div className="app-main-container">
            <Particles id="tsparticles" init={particlesInit} options={particlesConfig} />
            {/* The <Outlet> is a placeholder where React Router will render
                the matched child route (AppContent, AnalysisHistoryPage, or AccountPage) */}
            <Outlet />
        </div>
    );
};

// We need to use a component that has access to the router's location.
// So we create a small wrapper around our main logic.
const AppWithRouter = () => {
    const location = useLocation();
    const [showCookieModal, setShowCookieModal] = useState(false);
    
    // Notice we removed AuthContext from here!
    const { isPageBlurred } = useContext(UIContext);

    useEffect(() => {
        const hasConsented = getCookieConsentValue("VetraAICookieConsent") === "true";
        if (!hasConsented) {
            setShowCookieModal(true);
        }
    }, []);

    const handleConsentAccept = () => {
        Cookies.set("VetraAICookieConsent", "true", { expires: 150 });
        setShowCookieModal(false);
    };

    const policyPages = ['/privacy-policy', '/terms-of-service'];
    const onPolicyPage = policyPages.includes(location.pathname);
    const isVerificationPage = location.pathname.startsWith('/verify');
    
    // isPageBlurred is now safely handled by our new listener component
    const shouldBlurApp = (showCookieModal && !onPolicyPage && !isVerificationPage) || isPageBlurred;

    return (
        <>
            <div className={shouldBlurApp ? 'app-content-blurred' : ''}>
                <AppRoutes />
            </div>

            <CookieConsentModal
                isOpen={showCookieModal}
                onAccept={handleConsentAccept}
            />

            {/* This isolated component handles the modal and cross-tab locking without lagging the app */}
            <SessionExpirationListener />
        </>
    );
};
const AppRoutes = () => {
    const location = useLocation();

    useEffect(() => {
        const scrollableElement = document.querySelector('.app-wrapper') || document.querySelector('.landing-page-container') || document.querySelector('.app-main-container');
        if (scrollableElement) {
            scrollableElement.scrollTo(0, 0);
        }
    }, [location.pathname]);

    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route element={<GlobalLayout />}>

                    {/* --- BRANCH 1: PUBLIC, UNLOCKED ROUTES --- */}
                    {/* These routes will NEVER show the site lock. */}
                    <Route element={<InfoPageLayout />}>
                        <Route path="/verify" element={<PageWrapper><VerificationPage /></PageWrapper>} />
                        <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />
                        <Route path="/solutions-standard" element={<PageWrapper><IndustryApplications /></PageWrapper>} />
                        <Route path="/solutions-amazon" element={<PageWrapper><IndustryApplicationsAmazon /></PageWrapper>} />
                        <Route path="/solutions-finance" element={<PageWrapper><IndustryApplicationsFinance /></PageWrapper>} />
                        <Route path="/solutions-healthcare" element={<PageWrapper><IndustryApplicationsHealthcare /></PageWrapper>} />
                        <Route path="/enterprise" element={<PageWrapper><EnterprisePage /></PageWrapper>} />
                        <Route path="/features" element={<PageWrapper><FeaturesPage /></PageWrapper>} />
                        <Route path="/technology" element={<PageWrapper><TechnologyPage /></PageWrapper>} />
                        <Route path="/how-we-are-different" element={<PageWrapper><HowWeAreDifferentPage /></PageWrapper>} />
                        <Route path="/pricing" element={<PageWrapper><PricingPage /></PageWrapper>} />
                        <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
                        <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
                        <Route path="/privacy-policy" element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
                        <Route path="/blog" element={<PageWrapper><BlogIndexPage /></PageWrapper>} />
                        <Route path="/blog/:slug" element={<PageWrapper><BlogPostPage /></PageWrapper>} />
                        <Route path="/terms-of-service" element={<PageWrapper><TermsOfService /></PageWrapper>} />

                    </Route>

                    {/* --- BRANCH 2: PROTECTED ROUTES --- */}
                    {/* All routes inside this branch are wrapped by the SiteLockedLayout. */}
                    <Route element={<SiteLockedLayout />}>
                        <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />
                        <Route element={<InfoPageLayout />}>
                            <Route element={<PublicRoute />}>
                                <Route path="/login" element={<PageWrapper><AuthPage /></PageWrapper>} />
                            </Route>
                            <Route path="/reset-password/:token" element={<PageWrapper><ResetPasswordPage /></PageWrapper>} />
                        </Route>
                        <Route path="/initializing" element={<InitializationPage />} />

                        {/* The main application routes are nested under the new AppLayout */}
                        <Route element={<ProtectedRoute />}>
                            <Route path="/app" element={<AppLayout />}>
                                {/* The index route is the default page for "/app" */}
                                <Route index element={<AppContent />} />

                                {/* These are now child routes */}
                                <Route path="history" element={<AnalysisHistoryPage />} />
                                <Route path="account" element={<AccountPage />} />
                                <Route path="bulk-analysis" element={<BulkAnalysisPage />} />
                                <Route path="licenses" element={<LicenseManagementPage />} />
                                <Route path="billing" element={<BillingPage />} />
                                <Route path="threat-radar" element={<ThreatRadarPage />} />
                            </Route>
                        </Route>

                        {/* Fallback route to redirect any unknown paths to the homepage */}
                        <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                </Route>
            </Routes>
        </AnimatePresence>
    );
};

// The main App component is unchanged
function App() {
    return (
        <ThemeProvider theme={theme}>
            <HelmetProvider>
                <Router>
                    <AppProvider>
                        <AppWithRouter />
                    </AppProvider>
                </Router>
            </HelmetProvider>
        </ThemeProvider>
    );
}

export default App;